"use client";
import CallCenterIcon from "@/components/atoms/icons/CallCenterIcon";
import FacebookIcon from "@/components/atoms/icons/FacebookIcon";
import ZaloIcon from "@/components/atoms/icons/ZaloIcon";
import { CONTACT_DIRECTLY_BTNS } from "@/constants/values";
import { useRef, useState } from "react";

const ContactDirectly = () => {
  const [openTooltips, setOpenTooltips] = useState<{ [key: number]: boolean }>(
    {}
  );
  const tooltipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleMouseEnter = (idx: number) => {
    setOpenTooltips((prev) => ({ ...prev, [idx]: true }));
  };

  const handleMouseLeave = (idx: number) => {
    setTimeout(() => {
      const tooltipEl = tooltipRefs.current[idx];
      const buttonEl = buttonRefs.current[idx];

      if (
        tooltipEl &&
        !tooltipEl.matches(":hover") &&
        buttonEl &&
        !buttonEl.matches(":hover")
      ) {
        setOpenTooltips((prev) => ({ ...prev, [idx]: false }));
      }
    }, 100);
  };

  const renderIcon = (iconType: string): JSX.Element | null => {
    switch (iconType) {
      case "call":
        return (
          <span className="w-8 h-8 p-1 flex items-center justify-center text-rose">
            <CallCenterIcon />
          </span>
        );
      case "zalo":
        return (
          <span className="w-8 h-8 flex items-center justify-center text-blue">
            <ZaloIcon />
          </span>
        );
      case "facebook":
        return (
          <span className="w-8 h-8 flex items-center justify-center text-white !fill-primary">
            <FacebookIcon />
          </span>
        );
      default:
        return null;
    }
  };

  const buttons = CONTACT_DIRECTLY_BTNS();

  return (
    <div className="fixed bottom-36 right-2 md:bottom-28 md:right-5 z-50">
      <div className="flex flex-col mt-3 space-y-2">
        {buttons.map((button, idx) => (
          <div key={idx} className="relative">
            <button
              ref={(el) => {
                buttonRefs.current[idx] = el;
              }}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white shadow-lg flex items-center justify-center 
              hover:bg-primary-light hover:scale-110 transition-all duration-300 border border-gray-200"
              aria-label={button.label}
              onMouseEnter={() => handleMouseEnter(idx)}
              onMouseLeave={() => handleMouseLeave(idx)}
            >
              {renderIcon(button.iconType)}
            </button>

            <div
              ref={(el) => {
                tooltipRefs.current[idx] = el;
              }}
              className={`absolute right-16 md:right-20 bg-white rounded-lg shadow-lg p-3 min-w-[220px] z-50
              ${
                openTooltips[idx]
                  ? "!opacity-100"
                  : "!opacity-0 pointer-events-none"
              } 
              transition-all duration-300 animate-fade-in`}
              style={{
                [idx === 0 ? "bottom" : "top"]: "0px",
                transform: idx === 0 ? "translateY(-90%)" : "translateY(-10%)",
              }}
              onMouseEnter={() => handleMouseEnter(idx)}
              onMouseLeave={() => handleMouseLeave(idx)}
            >
              <div className="flex flex-col">
                <h3 className="text-primary font-semibold text-sm md:text-base mb-2 pb-1 border-b border-gray-100">
                  {button.label}
                </h3>
                <div className="flex flex-col space-y-2">
                  {button.children.map((child, childIdx) => (
                    <a
                      key={childIdx}
                      href={
                        button.iconType === "call"
                          ? `tel:${child.subtitle.replace(/\s/g, "")}`
                          : "#"
                      }
                      onClick={(e) => {
                        if (button.iconType !== "call") {
                          e.preventDefault();
                          child.action();
                        }
                      }}
                      className="cursor-pointer text-left text-sm md:text-base py-1.5 px-2.5 hover:bg-primary-light 
                      rounded-md transition-colors duration-200 flex items-center gap-2"
                    >
                      <span className="inline-block w-5 h-5 mr-2 mb-2.5">
                        {renderIcon(button.iconType)}
                      </span>
                      {child.subtitle}
                    </a>
                  ))}
                </div>
              </div>
              <div
                className={`absolute -right-2 ${
                  idx === 0 ? "bottom-4" : "top-4"
                } w-0 h-0 
                border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-white`}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactDirectly;
